'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import PricingCard from './PricingCard';
import ROICalculator from './ROICalculator';
import ComparisonTable from './ComparisonTable';
import BulkCustomerLogos from './BulkCustomerLogos';
import TestimonialCard from './TestimonialCard';
import FAQSection from './FAQSection';
import Icon from '@/components/ui/AppIcon';

type BillingCycle = 'HALF_YEARLY' | 'YEARLY';

interface PricingPlan {
  id: string;
  title: string;
  subtitle: string;
  prices: {
    HALF_YEARLY: { current: number; original?: number };
    YEARLY: { current: number; original?: number };
  } | null;
  features: Array<{ text: string; included: boolean }>;
  isPopular: boolean;
  isCustom?: boolean;
}

interface Testimonial {
  name: string;
  role: string;
  organization: string;
  image: string;
  alt: string;
  testimonial: string;
  rating: number;
}

const pricingPlans: PricingPlan[] = [
  {
    id: 'plus',
    title: 'ParkPing Plus',
    subtitle: 'Ideal for single vehicle owners',
    prices: {
      HALF_YEARLY: { current: 99, original: 109 },
      YEARLY: { current: 199, original: 218 },
    },
    features: [
      { text: 'Parking SMS notification', included: true },
      { text: 'Private contact protection', included: true },
      { text: 'Up to 15 alerts per plan', included: true },
      { text: 'Call details to vehicle owner', included: false },
    ],
    isPopular: false,
  },
  {
    id: 'prime',
    title: 'ParkPing Prime',
    subtitle: 'Most selected for daily city usage',
    prices: {
      HALF_YEARLY: { current: 199, original: 219 },
      YEARLY: { current: 349, original: 398 },
    },
    features: [
      { text: 'Parking SMS notification', included: true },
      { text: 'Private contact protection', included: true },
      { text: 'Up to 15 alerts per plan', included: true },
      { text: 'Call details to vehicle owner', included: true },
    ],
    isPopular: true,
  },
  {
    id: 'pro',
    title: 'ParkPing Pro',
    subtitle: 'For society and enterprise deployments',
    prices: null,
    features: [
      { text: 'Parking SMS notification', included: true },
      { text: 'Private contact protection', included: true },
      { text: 'Up to 15 alerts per plan', included: true },
      { text: 'Call details to vehicle owner', included: true },
      { text: 'Dedicated support', included: true },
    ],
    isPopular: false,
    isCustom: true,
  },
];

const testimonials: Testimonial[] = [
  {
    name: 'Rajesh Kumar',
    role: 'Society Secretary',
    organization: 'Prestige Lakeside Habitat',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_16f00d7ef-1763293540305.png',
    alt: 'Middle-aged Indian man in formal blue shirt smiling confidently in office setting',
    testimonial:
      'Implementing ParkPing across our society reduced parking complaints quickly. Owners now stay reachable without public number sharing.',
    rating: 5,
  },
  {
    name: 'Priya Sharma',
    role: 'Facilities Manager',
    organization: 'Tech Park Bangalore',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_123863fef-1763300606145.png',
    alt: 'Professional Indian woman in business attire with glasses in modern corporate office',
    testimonial:
      'Prime plan works best for our regular users. Communication is faster and privacy concerns are significantly lower.',
    rating: 5,
  },
  {
    name: 'Amit Patel',
    role: 'Resident',
    organization: 'DLF Garden City',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1b346009c-1763295401809.png',
    alt: 'Young Indian man in casual grey t-shirt smiling outdoors with natural lighting',
    testimonial:
      'The card solved daily parking friction for our family. People can reach us when needed, but our number stays private.',
    rating: 5,
  },
];

export default function PricingPlansInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('YEARLY');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handlePlanSelect = (planId: string) => {
    if (planId === 'pro') {
      window.location.href = '/support-hub';
      return;
    }
    window.location.href = '/get-started';
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-white">
        <div className="container mx-auto px-4 py-16">
          <div className="animate-pulse space-y-8">
            <div className="h-12 bg-gray-200 rounded w-3/4 mx-auto"></div>
            <div className="h-6 bg-gray-200 rounded w-1/2 mx-auto"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-96 bg-gray-100 rounded-2xl"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <section className="bg-gradient-to-b from-[#f5fbf6] to-background py-20 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-extrabold text-primary mb-6">
              Choose the Right ParkPing Plan
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8">
              Clear pricing with one trust-first message across all plans.
            </p>
            <div className="inline-flex p-1 rounded-xl bg-white border border-border shadow-subtle">
              <button
                type="button"
                onClick={() => setBillingCycle('HALF_YEARLY')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                  billingCycle === 'HALF_YEARLY' ? 'bg-accent text-accent-foreground' : 'text-muted-foreground'
                }`}
              >
                6 Months
              </button>
              <button
                type="button"
                onClick={() => setBillingCycle('YEARLY')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                  billingCycle === 'YEARLY' ? 'bg-accent text-accent-foreground' : 'text-muted-foreground'
                }`}
              >
                12 Months (Recommended)
              </button>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 text-muted-foreground mt-6">
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircleIcon" size={20} className="text-accent" />
                <span className="text-sm">Up to 15 alerts per plan</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircleIcon" size={20} className="text-accent" />
                <span className="text-sm">Private contact protection</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircleIcon" size={20} className="text-accent" />
                <span className="text-sm">No hidden fees</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 -mt-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {pricingPlans.map((plan) => {
              const selectedPrice = plan.prices?.[billingCycle];
              const priceLabel = plan.isCustom
                ? 'Custom'
                : `INR ${selectedPrice?.current?.toLocaleString('en-IN') || '0'}`;
              const periodLabel = plan.isCustom
                ? '/deployment'
                : `/card - ${billingCycle === 'YEARLY' ? '12 months' : '6 months'}`;

              return (
                <PricingCard
                  key={plan.id}
                  title={plan.title}
                  subtitle={plan.subtitle}
                  price={priceLabel}
                  period={periodLabel}
                  features={plan.features}
                  isPopular={plan.isPopular}
                  ctaText={plan.isCustom ? 'Contact Sales' : 'Get Started'}
                  onSelect={() => handlePlanSelect(plan.id)}
                />
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <BulkCustomerLogos />
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <ROICalculator />

            <div className="space-y-6">
              <div className="bg-accent/5 rounded-2xl p-8 border border-accent/20">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="bg-accent/10 p-3 rounded-lg flex-shrink-0">
                    <Icon name="ShieldCheckIcon" size={24} className="text-accent" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-bold text-gray-900 mb-2">Low-Risk Start</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Start with Plus, move to Prime anytime. Upgrade path is simple and friction-free.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-accent/5 rounded-2xl p-8 border border-accent/20">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="bg-accent/10 p-3 rounded-lg flex-shrink-0">
                    <Icon name="SparklesIcon" size={24} className="text-accent" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-bold text-gray-900 mb-2">Clear Feature Ladder</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Prime adds call details to owner, while Pro includes dedicated support for organizations.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-accent/5 rounded-2xl p-8 border border-accent/20">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="bg-accent/10 p-3 rounded-lg flex-shrink-0">
                    <Icon name="BoltIcon" size={24} className="text-accent" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-bold text-gray-900 mb-2">Built for Fast Action</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Designed for temporary parking situations where fast owner reach-out prevents conflict and damage.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <ComparisonTable />
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4">
              What Our Customers Say
            </h2>
            <p className="text-lg text-gray-600">Real experiences from real customers across India</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <TestimonialCard key={index} {...testimonial} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <FAQSection />
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="bg-white rounded-3xl p-12 md:p-16 text-center shadow-card border border-border">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-extrabold text-primary mb-6">
              Ready to Protect Your Vehicle Privacy?
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground mb-10 max-w-3xl mx-auto">
              Choose Plus or Prime for immediate use. Choose Pro for society and enterprise rollout.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <Link
                href="/get-started"
                className="px-10 py-5 bg-accent text-accent-foreground font-heading font-bold text-lg rounded-xl shadow-cta hover:shadow-cta-hover hover:-translate-y-1 transition-all duration-300"
              >
                Get Your Card Now
              </Link>
              <Link
                href="/support-hub"
                className="px-10 py-5 bg-white border border-border text-primary font-heading font-bold text-lg rounded-xl hover:bg-muted transition-all duration-300"
              >
                Talk to Sales
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
